/**
 * ClarificationsSection — Berater-Sicht im Cockpit für den Rückfragen-Channel.
 *
 * Drei Tabs:
 *   1. „Offene Rückfragen" (default): Mandant hat noch nicht geantwortet
 *      AUF Berater-Frage, ODER Mandant hat eine eigene Frage gestellt.
 *      Berater kann auf Mandanten-Fragen antworten oder Berater-eigene
 *      Fragen direkt resolven, falls sie sich erübrigt haben.
 *   2. „Beantwortet · warten auf Freigabe": Antworten liegen vor, Berater
 *      muss als gelöst markieren.
 *   3. „Gelöst (Verlauf)": vollständiger Verlauf für Audit/Beweisspur.
 *
 * Außerdem: „+ Neue Rückfrage stellen" — Modal mit Field-Picker, gruppiert
 * nach Wizard-Step (Phase B). field_label kommt aus dem zentralen
 * FIELD_HELP-Repository (entry.label) statt aus jedem Aufrufer einzeln.
 *
 * Restriktionen:
 *   - Berater darf SEINE EIGENE Frage NICHT beantworten (Trigger 0017
 *     blockt). Wir zeigen den Antwort-Button nur, wenn die Frage vom
 *     Mandanten kommt.
 *   - Resolve dürfen nur Berater/Admin (Migration 0017). UI-Button immer
 *     sichtbar — wenn Resolve fehlschlägt, wird die Fehlermeldung gezeigt.
 *
 * Phase B / WCAG 2.1 AA:
 *   - Modal hat Focus-Trap (useFocusTrap), ESC schließt
 *   - Form-Inputs mit korrekter <label htmlFor>-Verknüpfung
 *   - Suchfeld + optgroups für Felder-Auswahl bei wachsendem Repo
 *   - Demo-Modus: zeigt Mock-Fixtures, kein DB-Roundtrip
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquarePlus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { de } from '../../lib/i18n/de';
import {
  answerClarification,
  createClarification,
  listClarificationsForCase,
  resolveClarification,
  type CaseClarification,
} from '../../lib/supabaseClarifications';
import { FIELD_HELP, type FieldHelpKey } from '../../lib/i18n/fieldHelp';
import { DEMO_CLARIFICATIONS, isDemoCaseId } from '../../lib/demo/demoFixtures';
import { useFocusTrap } from '../a11y/useFocusTrap';
import { useConfirm } from '../ui/ConfirmDialog';
import { LoadingRows } from '../ui/Skeleton';
import { Button } from '../ui/Button';

interface ClarificationsSectionProps {
  readonly caseId: string;
  /**
   * Optional: Wizard-URL-Präfix für „Im Wizard öffnen"-Links.
   * Default: `/case/{caseId}/wizard` (Berater-Pfad).
   */
  readonly wizardUrlBase?: string;
}

type Tab = 'open' | 'awaiting' | 'resolved';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: ReadonlyArray<CaseClarification> }
  | { kind: 'error'; message: string };

export function ClarificationsSection({ caseId, wizardUrlBase }: ClarificationsSectionProps) {
  const base = wizardUrlBase ?? `/case/${caseId}/wizard`;
  const [tab, setTab] = useState<Tab>('open');
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [newOpen, setNewOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoad({ kind: 'loading' });
    // Phase B: Demo-Case → Mock-Fixtures, kein DB-Roundtrip.
    if (isDemoCaseId(caseId)) {
      setLoad({ kind: 'ready', items: DEMO_CLARIFICATIONS });
      return;
    }
    try {
      const items = await listClarificationsForCase(caseId);
      setLoad({ kind: 'ready', items });
    } catch (err) {
      setLoad({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Konnte Rückfragen nicht laden.',
      });
    }
  }, [caseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (load.kind !== 'ready') return [];
    if (tab === 'open') {
      return load.items.filter((c) => c.answered_at === null && c.resolved_at === null);
    }
    if (tab === 'awaiting') {
      return load.items.filter((c) => c.answered_at !== null && c.resolved_at === null);
    }
    return load.items.filter((c) => c.resolved_at !== null);
  }, [load, tab]);

  return (
    <section
      className="mb-10 rounded-sm border border-stone-200 bg-white"
      aria-labelledby="clarifications-section-title"
    >
      <div className="flex flex-col gap-3 border-b border-stone-200 px-6 py-5 md:flex-row md:items-start md:justify-between md:px-10">
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.cockpitClarifications.sectionLabel}
          </p>
          <h2
            id="clarifications-section-title"
            className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900"
          >
            {de.cockpitClarifications.sectionTitle}
          </h2>
          <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-600">
            {de.cockpitClarifications.sectionIntro}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            setNewOpen(true);
          }}
        >
          <MessageSquarePlus size={14} aria-hidden="true" />
          {de.cockpitClarifications.newButton}
        </Button>
      </div>

      {/* Tabs */}
      <div
        className="flex flex-wrap items-center gap-1 border-b border-stone-200 px-3 py-2 md:px-7"
        role="tablist"
        aria-label="Rückfragen-Filter"
      >
        <TabBtn active={tab === 'open'} onClick={() => setTab('open')}>
          {de.cockpitClarifications.tabOpen}
          {load.kind === 'ready' && (
            <CountBadge
              count={
                load.items.filter((c) => c.answered_at === null && c.resolved_at === null).length
              }
            />
          )}
        </TabBtn>
        <TabBtn active={tab === 'awaiting'} onClick={() => setTab('awaiting')}>
          {de.cockpitClarifications.tabAwaiting}
          {load.kind === 'ready' && (
            <CountBadge
              count={
                load.items.filter((c) => c.answered_at !== null && c.resolved_at === null).length
              }
            />
          )}
        </TabBtn>
        <TabBtn active={tab === 'resolved'} onClick={() => setTab('resolved')}>
          {de.cockpitClarifications.tabResolved}
        </TabBtn>
      </div>

      <div className="px-6 py-6 md:px-10">
        {load.kind === 'loading' && <LoadingRows rows={2} label="Rückfragen werden geladen" />}
        {load.kind === 'error' && (
          <p
            role="alert"
            className="rounded-sm border border-red-200 bg-red-50 px-4 py-2 font-serif text-sm text-red-800"
          >
            {load.message}
          </p>
        )}
        {load.kind === 'ready' && filtered.length === 0 && (
          <p className="font-serif text-sm italic text-stone-500">
            {tab === 'open'
              ? de.cockpitClarifications.emptyOpen
              : tab === 'awaiting'
                ? de.cockpitClarifications.emptyAwaiting
                : de.cockpitClarifications.emptyResolved}
          </p>
        )}
        {load.kind === 'ready' && filtered.length > 0 && (
          <ul className="space-y-3">
            {filtered.map((c) => (
              <ClarificationItem
                key={c.id}
                clarification={c}
                wizardUrlBase={base}
                onChange={() => {
                  void refresh();
                }}
              />
            ))}
          </ul>
        )}
      </div>

      {newOpen && (
        <NewClarificationModal
          caseId={caseId}
          onClose={() => {
            setNewOpen(false);
          }}
          onCreated={() => {
            setNewOpen(false);
            void refresh();
          }}
        />
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Item
// -----------------------------------------------------------------------------

function ClarificationItem({
  clarification,
  wizardUrlBase,
  onChange,
}: {
  clarification: CaseClarification;
  wizardUrlBase: string;
  onChange: () => void;
}) {
  const confirm = useConfirm();
  const [showAnswer, setShowAnswer] = useState(false);
  const [answer, setAnswer] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Berater darf eine EIGENE Frage nicht beantworten (Trigger 0017).
  const canAnswer =
    clarification.created_by_role === 'mandant' && clarification.answered_at === null;
  const canResolve = clarification.resolved_at === null;

  const submitAnswer = async () => {
    setWorking(true);
    setError(null);
    try {
      await answerClarification(clarification.id, answer);
      setShowAnswer(false);
      setAnswer('');
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : de.cockpitClarifications.error);
    } finally {
      setWorking(false);
    }
  };

  const submitResolve = async () => {
    const proceed = await confirm({
      title: de.confirmDialog.resolveClarificationTitle,
      body: de.cockpitClarifications.resolveConfirm,
      confirmLabel: de.confirmDialog.resolveClarificationConfirmLabel,
    });
    if (!proceed) {
      return;
    }
    setWorking(true);
    setError(null);
    try {
      await resolveClarification(clarification.id);
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : de.cockpitClarifications.error);
    } finally {
      setWorking(false);
    }
  };

  // Wizard-Link-Heuristik: aus pathKey wie "resources.bwa.revenue" leiten
  // wir den Wizard-Step "resources" ab. Funktioniert für alle aktuellen
  // FieldHelp-Keys (kein Stammdaten-Routing — das hat kein Step-Slug).
  const wizardStep = clarification.field_path.split('.')[0];
  const isKnownStep = [
    'triage',
    'identity',
    'activity',
    'style',
    'resources',
    'health',
    'private',
  ].includes(wizardStep ?? '');
  const wizardLink = isKnownStep ? `${wizardUrlBase}/${wizardStep}` : null;

  const askedByLabel =
    clarification.created_by_role === 'mandant'
      ? de.cockpitClarifications.askedByMandant
      : de.cockpitClarifications.askedByBerater;

  return (
    <li className="rounded-sm border border-stone-200 bg-white">
      <div className="px-4 py-3 md:px-5 md:py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span
            className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${
              clarification.created_by_role === 'mandant'
                ? 'bg-amber-100 text-amber-900'
                : 'bg-stone-100 text-stone-800'
            }`}
          >
            {askedByLabel}
          </span>
          <span className="font-sans text-[10px] uppercase tracking-widest text-stone-600">
            {de.cockpitClarifications.askedAt.replace(
              '{date}',
              new Date(clarification.created_at).toLocaleString('de-DE'),
            )}
          </span>
          {clarification.resolved_at && (
            <span className="inline-flex items-center rounded-sm bg-emerald-100 px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest text-emerald-900">
              {de.cockpitClarifications.resolvedAt.replace(
                '{date}',
                new Date(clarification.resolved_at).toLocaleDateString('de-DE'),
              )}
            </span>
          )}
        </div>

        {clarification.field_label && (
          <p className="mt-2 font-sans text-[11px] uppercase tracking-widest text-stone-600">
            {de.cockpitClarifications.onField}:{' '}
            <span className="text-stone-800">{clarification.field_label}</span>
          </p>
        )}

        <p className="mt-2 font-serif text-base leading-relaxed text-stone-900">
          {clarification.question}
        </p>

        {clarification.answer && clarification.answered_at && (
          <div className="mt-3 rounded-sm border-l-2 border-emerald-300 bg-emerald-50 px-3 py-2">
            <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-emerald-900">
              {de.cockpitClarifications.answeredAt.replace(
                '{date}',
                new Date(clarification.answered_at).toLocaleString('de-DE'),
              )}
            </p>
            <p className="mt-1 font-serif text-sm text-emerald-950">{clarification.answer}</p>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {wizardLink && (
            <Link
              to={wizardLink}
              className="inline-flex items-center gap-1 font-sans text-[11px] font-medium uppercase tracking-widest text-stone-700 underline-offset-2 hover:text-stone-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {de.cockpitClarifications.jumpToWizard}
            </Link>
          )}
          {canAnswer && !showAnswer && (
            <button
              type="button"
              onClick={() => {
                setShowAnswer(true);
              }}
              className="inline-flex min-h-[36px] items-center rounded-sm border border-stone-200 bg-white px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-stone-700 hover:border-stone-900 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {de.cockpitClarifications.answerInline}
            </button>
          )}
          {canResolve && (
            <button
              type="button"
              disabled={working}
              onClick={() => {
                void submitResolve();
              }}
              className="inline-flex min-h-[36px] items-center rounded-sm border border-emerald-300 bg-emerald-50 px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-emerald-900 hover:bg-emerald-100 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
            >
              {working
                ? de.cockpitClarifications.resolving
                : de.cockpitClarifications.resolveButton}
            </button>
          )}
        </div>

        {showAnswer && (
          <div className="mt-3 rounded-sm border border-stone-200 bg-stone-50 px-3 py-3">
            <label
              htmlFor={`answer-${clarification.id}`}
              className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-600"
            >
              {de.cockpitClarifications.answerLabel}
            </label>
            <textarea
              id={`answer-${clarification.id}`}
              value={answer}
              onChange={(e) => {
                setAnswer(e.target.value);
              }}
              placeholder={de.cockpitClarifications.answerPlaceholder}
              rows={3}
              className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-3 py-2 font-serif text-sm text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
            />
            <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowAnswer(false);
                  setAnswer('');
                }}
                className="inline-flex min-h-[36px] items-center rounded-sm border border-stone-200 bg-white px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              >
                {de.cockpitClarifications.newCancel}
              </button>
              <button
                type="button"
                disabled={working || answer.trim().length === 0}
                onClick={() => {
                  void submitAnswer();
                }}
                className="inline-flex min-h-[36px] items-center rounded-sm bg-stone-900 px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              >
                {working ? de.cockpitClarifications.sending : de.cockpitClarifications.sendAnswer}
              </button>
            </div>
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="mt-2 rounded-sm border border-red-200 bg-red-50 px-3 py-2 font-serif text-sm text-red-800"
          >
            {error}
          </p>
        )}
      </div>
    </li>
  );
}

// -----------------------------------------------------------------------------
// Modal: neue Rückfrage anlegen
// -----------------------------------------------------------------------------

/**
 * Phase B: FieldPicker gruppiert nach Wizard-Step (statt flacher Liste mit
 * 23+ Einträgen). Optionales Live-Filter-Suchfeld macht das Finden auch
 * bei wachsendem Repo schnell.
 */

const STEP_GROUP_LABELS: Readonly<Record<string, string>> = {
  stammdaten: 'Stammdaten (Modul 00)',
  triage: 'Triage (Modul 01)',
  identity: 'Identität & Qualifikation (Modul 02)',
  activity: 'Aktivität & Geschäftsmodell (Modul 03)',
  style: 'Stil & Marktposition (Modul 04)',
  resources: 'Ressourcen & Finanzen (Modul 05)',
  health: 'Gesundheit & Resilienz (Modul 06)',
  private: 'Privates Umfeld (Modul 07)',
};

const STEP_GROUP_ORDER: ReadonlyArray<string> = [
  'stammdaten',
  'triage',
  'identity',
  'activity',
  'style',
  'resources',
  'health',
  'private',
];

interface GroupedFieldOption {
  readonly key: FieldHelpKey;
  readonly label: string;
}

interface FieldGroup {
  readonly step: string;
  readonly stepLabel: string;
  readonly options: ReadonlyArray<GroupedFieldOption>;
}

function buildGroupedFieldOptions(searchTerm: string = ''): ReadonlyArray<FieldGroup> {
  const term = searchTerm.trim().toLowerCase();

  // Pro Step ein Bucket sammeln; danach in der STEP_GROUP_ORDER ausgeben.
  const buckets: Record<string, GroupedFieldOption[]> = {};
  for (const [key, entry] of Object.entries(FIELD_HELP) as Array<
    [FieldHelpKey, (typeof FIELD_HELP)[FieldHelpKey]]
  >) {
    // Step ableiten: explizit aus entry.wizardStep, sonst erstes Pfad-Segment.
    const step = entry.wizardStep ?? key.split('.')[0] ?? 'sonstiges';
    const label = entry.label;
    if (term.length > 0) {
      // Live-Filter: matche auf Label, Path und entry.what
      const haystack = `${label} ${key} ${entry.what}`.toLowerCase();
      if (!haystack.includes(term)) continue;
    }
    (buckets[step] ??= []).push({ key, label });
  }

  // Stabile Sortierung pro Bucket (alphabetisch nach Label).
  for (const step of Object.keys(buckets)) {
    buckets[step]?.sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }

  // Reihenfolge der Buckets nach STEP_GROUP_ORDER, dann alles, was sonst
  // noch da ist (defensive für unbekannte Steps).
  const orderedSteps = [
    ...STEP_GROUP_ORDER.filter((s) => buckets[s] !== undefined),
    ...Object.keys(buckets).filter((s) => !STEP_GROUP_ORDER.includes(s)),
  ];

  return orderedSteps.map((step) => ({
    step,
    stepLabel: STEP_GROUP_LABELS[step] ?? step,
    options: buckets[step] ?? [],
  }));
}

function NewClarificationModal({
  caseId,
  onClose,
  onCreated,
}: {
  caseId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [field, setField] = useState<FieldHelpKey | ''>('');
  const [question, setQuestion] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const dialogRef = useRef<HTMLDivElement>(null);
  // WCAG 2.1 AA: Focus-Trap. Initial-Fokus aufs Suchfeld (häufigste Aktion).
  useFocusTrap(dialogRef, { active: true });

  // ESC schließt
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const groups = useMemo(() => buildGroupedFieldOptions(search), [search]);

  const submit = async () => {
    if (field === '') {
      setError('Bitte wählen Sie ein Feld.');
      return;
    }
    setWorking(true);
    setError(null);
    try {
      // Phase B: fieldLabel aus dem zentralen Repository (entry.label).
      const entry = (FIELD_HELP as Readonly<Record<string, { label: string }>>)[field];
      await createClarification({
        caseId,
        fieldPath: field,
        fieldLabel: entry?.label ?? field,
        question,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : de.cockpitClarifications.newError);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4 py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-clarification-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg rounded-sm border border-stone-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b border-stone-200 px-5 py-4 md:px-6">
          <h3
            id="new-clarification-title"
            className="font-serif text-xl font-bold tracking-tight text-stone-900"
          >
            {de.cockpitClarifications.newModalTitle}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={de.cockpitClarifications.newCancel}
            className="rounded-sm p-1 text-stone-600 hover:bg-stone-100 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="px-5 py-5 md:px-6">
          <label
            htmlFor="new-clarification-field"
            className="font-sans text-xs font-medium uppercase tracking-widest text-stone-600"
          >
            {de.cockpitClarifications.newFieldLabel}
          </label>
          {/* Suchfeld zur schnellen Filterung */}
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            placeholder={'Feld suchen (z.B. „Umsatz", „BWA", „Kinder") …'}
            aria-label="Felder filtern"
            className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-4 py-2.5 font-serif text-sm text-stone-900 placeholder:italic placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
          />
          <select
            id="new-clarification-field"
            value={field}
            onChange={(e) => {
              setField(e.target.value as FieldHelpKey | '');
            }}
            size={Math.min(
              10,
              groups.reduce((acc, g) => acc + g.options.length + 1, 1),
            )}
            className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-4 py-2 font-serif text-sm text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
          >
            <option value="">{de.cockpitClarifications.newFieldPlaceholder}</option>
            {groups.map((g) => (
              <optgroup key={g.step} label={g.stepLabel}>
                {g.options.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {groups.every((g) => g.options.length === 0) && (
            <p className="mt-2 font-serif text-xs italic text-stone-600">
              Keine Felder gefunden für „{search}".
            </p>
          )}

          <label
            htmlFor="new-clarification-question"
            className="mt-5 block font-sans text-xs font-medium uppercase tracking-widest text-stone-600"
          >
            {de.cockpitClarifications.newQuestionLabel}
          </label>
          <textarea
            id="new-clarification-question"
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
            }}
            placeholder={de.cockpitClarifications.newQuestionPlaceholder}
            rows={4}
            className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
          />

          {error && (
            <p
              role="alert"
              className="mt-3 rounded-sm border border-red-200 bg-red-50 px-3 py-2 font-serif text-sm text-red-800"
            >
              {error}
            </p>
          )}

          <div className="mt-5 flex flex-col items-stretch justify-end gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {de.cockpitClarifications.newCancel}
            </button>
            <button
              type="button"
              disabled={working || question.trim().length === 0 || field === ''}
              onClick={() => {
                void submit();
              }}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-stone-900 px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {working ? de.cockpitClarifications.sending : de.cockpitClarifications.newSubmit}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`inline-flex min-h-[44px] items-center rounded-sm px-4 py-2 font-sans text-xs font-medium uppercase tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 ${
        active
          ? 'bg-stone-900 text-white'
          : 'bg-white text-stone-700 hover:bg-stone-100 hover:text-stone-900'
      }`}
    >
      {children}
    </button>
  );
}

function CountBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span
      aria-label={`${String(count)} Einträge`}
      className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-stone-200 px-1.5 font-mono text-[10px] text-stone-800"
    >
      {count}
    </span>
  );
}
